import { JSX, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text } from '@chakra-ui/react'
import { OrderItem, OrderPaymentStatus, PickupStatus } from '@leid/api-orders'

import { Loader } from 'components/ui/Loader'
import theme from 'styles/theme'
import { utcNow } from 'utils/dates'

import { getOrderItemFromDate, getOrderItemToDate } from '../utils/orderDates'

import { OrderItemCircleStatus } from './models'
import OrderItemStatusCircle from './StatusCircle'

type Props = {
  orderIsLoading: boolean
  orderItem: OrderItem
}

type CirclesPickupStatus = {
  paidStatus: OrderItemCircleStatus
  pickedUpStatus: OrderItemCircleStatus
  returnedStatus: OrderItemCircleStatus | undefined
}

const OrderItemPickupStatus = ({ orderItem, orderIsLoading }: Props): JSX.Element => {
  const { t } = useTranslation(['common'])

  const [pickupStatus, setPickupStatus] = useState<CirclesPickupStatus | undefined>(undefined)

  useEffect(() => {
    if (orderIsLoading) {
      setPickupStatus(undefined)
      return
    }

    calculatePickupStatus()
  }, [orderItem, orderIsLoading])

  const calculatePickupStatus = () => {
    const isConsumable = orderItem.productData?.complementary ?? false

    if (
      orderItem.pickupStatus === PickupStatus.RETURNED ||
      orderItem.pickupStatus === PickupStatus.INSPECTED ||
      orderItem.pickupStatus === PickupStatus.COMPLETED
    ) {
      setPickupStatus({
        paidStatus: OrderItemCircleStatus.PASSED,
        pickedUpStatus: OrderItemCircleStatus.PASSED,
        returnedStatus: !isConsumable ? OrderItemCircleStatus.CURRENT : undefined,
      })
      return
    }

    if (orderItem.pickupStatus === PickupStatus.PICKEDUP) {
      const now = utcNow()
      const toDate = getOrderItemToDate(orderItem)

      const returnedStatus =
        toDate && toDate.getTime() >= now.getTime() ? OrderItemCircleStatus.AVAILABLE : OrderItemCircleStatus.NONE
      setPickupStatus({
        paidStatus: OrderItemCircleStatus.PASSED,
        pickedUpStatus: OrderItemCircleStatus.CURRENT,
        returnedStatus: !isConsumable ? returnedStatus : undefined,
      })
      return
    }

    if (orderItem.paymentStatus === OrderPaymentStatus.paid) {
      const now = utcNow()
      const fromDate = getOrderItemFromDate(orderItem)

      const pickedUpStatus =
        fromDate && fromDate.getTime() <= now.getTime() ? OrderItemCircleStatus.AVAILABLE : OrderItemCircleStatus.NONE
      setPickupStatus({
        paidStatus: OrderItemCircleStatus.CURRENT,
        pickedUpStatus: pickedUpStatus,
        returnedStatus: !isConsumable ? OrderItemCircleStatus.NONE : undefined,
      })
      return
    }

    setPickupStatus({
      paidStatus: OrderItemCircleStatus.NONE,
      pickedUpStatus: OrderItemCircleStatus.NONE,
      returnedStatus: !isConsumable ? OrderItemCircleStatus.NONE : undefined,
    })
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      gridGap="4px"
      padding="10px"
      borderRadius="big"
      backgroundColor="leid.lysGul"
      width="100%"
    >
      {(orderIsLoading || !pickupStatus) && (
        <>
          <Loader margin={0} size="md" />
        </>
      )}

      {pickupStatus && (
        <>
          <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" gridGap="4px">
            <OrderItemStatusCircle circleStatus={pickupStatus.paidStatus} />

            <Box
              border="0.807px solid"
              borderColor={
                pickupStatus.pickedUpStatus !== OrderItemCircleStatus.NONE
                  ? theme.token('colors.leid.sort')
                  : theme.token('colors.leid.gr')
              }
              width="46px"
            ></Box>

            <OrderItemStatusCircle circleStatus={pickupStatus.pickedUpStatus} />

            {pickupStatus.returnedStatus && (
              <>
                <Box
                  border="0.807px solid"
                  borderColor={
                    pickupStatus.returnedStatus !== OrderItemCircleStatus.NONE
                      ? theme.token('colors.leid.sort')
                      : theme.token('colors.leid.gr')
                  }
                  width="46px"
                ></Box>
                <OrderItemStatusCircle circleStatus={pickupStatus.returnedStatus} />
              </>
            )}
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" gridGap="40px">
            <Text
              fontWeight={pickupStatus.paidStatus === OrderItemCircleStatus.CURRENT ? 700 : 400}
              fontSize="sm"
              textAlign="center"
            >
              {t('orderedStatus')}
            </Text>

            <Text
              fontWeight={pickupStatus.pickedUpStatus === OrderItemCircleStatus.CURRENT ? 700 : 400}
              fontSize="sm"
              textAlign="center"
            >
              {t('pickedupStatus')}
            </Text>

            {pickupStatus.returnedStatus && (
              <Text
                fontWeight={pickupStatus.returnedStatus === OrderItemCircleStatus.CURRENT ? 700 : 400}
                fontSize="sm"
                textAlign="center"
              >
                {t('returnedStatus')}
              </Text>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

export default OrderItemPickupStatus
